import {
  defineMessages,
  type IntlShape,
  type MessageDescriptor,
} from "react-intl";

import type { FacilityCategoryKey } from "@/features/hotels/utils/facilities/types";

const CATEGORY_MESSAGES = defineMessages<FacilityCategoryKey>({
  "core-essentials": {
    defaultMessage: "Core Essentials",
    description: "Hotel facilities category heading for core essentials",
  },
  accessibility: {
    defaultMessage: "Accessibility",
    description: "Hotel facilities category heading for accessibility",
  },
  safety: {
    defaultMessage: "Safety",
    description: "Hotel facilities category heading for safety",
  },
  leisure: {
    defaultMessage: "Leisure",
    description: "Hotel facilities category heading for leisure",
  },
  dining: {
    defaultMessage: "Dining",
    description: "Hotel facilities category heading for dining",
  },
  service: {
    defaultMessage: "Service",
    description: "Hotel facilities category heading for service",
  },
  sports: {
    defaultMessage: "Sports",
    description: "Hotel facilities category heading for sports",
  },
  "core-support": {
    defaultMessage: "Core Support",
    description: "Hotel facilities category heading for core support",
  },
  wellness: {
    defaultMessage: "Wellness",
    description: "Hotel facilities category heading for wellness",
  },
  family: {
    defaultMessage: "Family",
    description: "Hotel facilities category heading for family",
  },
  business: {
    defaultMessage: "Business",
    description: "Hotel facilities category heading for business",
  },
  entertainment: {
    defaultMessage: "Entertainment",
    description: "Hotel facilities category heading for entertainment",
  },
  secondary: {
    defaultMessage: "Secondary",
    description: "Hotel facilities category heading for secondary",
  },
  policies: {
    defaultMessage: "Policies",
    description: "Hotel facilities category heading for policies",
  },
  admin: {
    defaultMessage: "Admin",
    description: "Hotel facilities category heading for admin",
  },
  extra: {
    defaultMessage: "Extra",
    description: "Hotel facilities category heading for unmatched facilities",
  },
});

const FACILITY_MESSAGES = defineMessages({
  wifi: {
    defaultMessage: "Wifi",
    description: "Hotel facility label for wifi",
  },
  "air-conditioning-public-areas": {
    defaultMessage: "AC",
    description: "Hotel facility label for air conditioning in public areas",
  },
  "24-hour-reception": {
    defaultMessage: "24-Hours Front Desk",
    description: "Hotel facility label for 24-hour reception",
  },
  "towels-and-bed-linen": {
    defaultMessage: "Towels and bed linen",
    description: "Hotel facility label for towels and bed linen",
  },
  "lift-access": {
    defaultMessage: "Lift access",
    description: "Hotel facility label for lift access",
  },
  "car-park": {
    defaultMessage: "Car park",
    description: "Hotel facility label for car park",
  },
  "secure-parking": {
    defaultMessage: "Secure parking",
    description: "Hotel facility label for secure parking",
  },
  "valet-parking": {
    defaultMessage: "Valet parking",
    description: "Hotel facility label for valet parking",
  },
  "wheelchair-accessible": {
    defaultMessage: "Wheelchair-accessible",
    description: "Hotel facility label for wheelchair accessible",
  },
  "room-service": {
    defaultMessage: "Room service",
    description: "Hotel facility label for room service",
  },
  "laundry-service": {
    defaultMessage: "Laundry service",
    description: "Hotel facility label for laundry service",
  },
  "luggage-room": {
    defaultMessage: "Luggage room",
    description: "Hotel facility label for luggage room",
  },
  "airport-shuttle": {
    defaultMessage: "Airport shuttle",
    description: "Hotel facility label for airport shuttle",
  },
  "car-hire": {
    defaultMessage: "Car hire",
    description: "Hotel facility label for car hire",
  },
  gym: { defaultMessage: "Gym", description: "Hotel facility label for gym" },
  restaurant: {
    defaultMessage: "Restaurant",
    description: "Hotel facility label for restaurant",
  },
  "swimming-pool": {
    defaultMessage: "Swimming Pool",
    description: "Hotel facility label for swimming pool",
  },
  breakfast: {
    defaultMessage: "Breakfast",
    description: "Hotel facility label for breakfast",
  },
  "multilingual-staff": {
    defaultMessage: "Multilingual staff",
    description: "Hotel facility label for multilingual staff",
  },
  "24-hour-security": {
    defaultMessage: "24-hour security",
    description: "Hotel facility label for 24-hour security",
  },
  "spa-centre": {
    defaultMessage: "Spa centre",
    description: "Hotel facility label for spa centre",
  },
  sauna: {
    defaultMessage: "Sauna",
    description: "Hotel facility label for sauna",
  },
  bar: { defaultMessage: "Bar", description: "Hotel facility label for bar" },
  concierge: {
    defaultMessage: "Concierge",
    description: "Hotel facility label for concierge",
  },
  "babysitting-service": {
    defaultMessage: "Babysitting service",
    description: "Hotel facility label for babysitting service",
  },
  "children-playground": {
    defaultMessage: "Children playground",
    description: "Hotel facility label for children playground",
  },
  "business-centre": {
    defaultMessage: "Business centre",
    description: "Hotel facility label for business centre",
  },
  "meeting-room": {
    defaultMessage: "Meeting room",
    description: "Hotel facility label for meeting room",
  },
  casino: {
    defaultMessage: "Casino",
    description: "Hotel facility label for casino",
  },
  nightclub: {
    defaultMessage: "Nightclub",
    description: "Hotel facility label for nightclub",
  },
  "electric-kettle": {
    defaultMessage: "Electric kettle",
    description: "Hotel facility label for electric kettle",
  },
  garage: {
    defaultMessage: "Garage",
    description: "Hotel facility label for garage",
  },
  tennis: {
    defaultMessage: "Tennis",
    description: "Hotel facility label for tennis",
  },
  "small-pets-allowed": {
    defaultMessage: "Small pets allowed",
    description: "Hotel facility label for small pets allowed",
  },
  "check-in-check-out-key-collection": {
    defaultMessage: "Check-in / Check-out / Key Collection",
    description: "Hotel facility label for check-in check-out key collection",
  },
});

const formatLocalizedLabel = (
  intl: IntlShape,
  descriptor: MessageDescriptor | undefined,
  fallbackLabel: string,
): string => {
  if (!descriptor) {
    return fallbackLabel;
  }

  return intl.formatMessage(descriptor);
};

export const getLocalizedCategoryLabel = (
  intl: IntlShape,
  categoryKey: FacilityCategoryKey,
  fallbackLabel: string,
): string =>
  formatLocalizedLabel(intl, CATEGORY_MESSAGES[categoryKey], fallbackLabel);

export const getLocalizedFacilityLabel = (
  intl: IntlShape,
  facilityKey: string | undefined,
  fallbackLabel: string,
): string => {
  if (!facilityKey) {
    return fallbackLabel;
  }

  return formatLocalizedLabel(
    intl,
    FACILITY_MESSAGES[facilityKey as keyof typeof FACILITY_MESSAGES],
    fallbackLabel,
  );
};
